import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useMemo, useReducer, useState } from 'react';

import { GetEnvironmentDataResponse, User } from '@src/gen/ai/h2o/cloud/appstore/v1/ai.h2o.cloud.appstore';
import { ILeftPanelProps } from '@shared-legacy/lib/ui/components/LeftPanel/LeftPanel';
import type { AppUser, MenuItem } from '@shared-legacy/ui-kit';

import type { Banner, BannerAction } from './models';

/** Shape of `env.menu` on EnvironmentAndMenu (auth / shell menu flags). Lives here so shared-legacy never depends on app Navigation types. */
export interface AppStoreMenuInfo {
  user?: AppUser;
  externalMenuItems?: MenuItem[];
  bookTimeLink?: string;
  hasHomePage?: boolean;
  hasAiEngineManager?: boolean;
  hasMyApp?: boolean;
  hasPlatformUsage?: boolean;
  hasBookTime?: boolean;
  url?: string;
  hasMenu?: boolean;
  hasAdminMarketPlaceApps?: boolean;
  hasAdminMenus?: boolean;
  hasSuperAdminMenus?: boolean;
  hasVisitorMode?: boolean;
  isPublicMode?: boolean;
  isVisitorMode?: boolean;
  environmentName?: string;
  hasNotebook?: boolean;
  hasSecureStore?: boolean;
  cliDownloadEnabled?: boolean;
  adminCenterEnabled?: boolean;
  hasAuditTrail: boolean;
  aiUnitConsumptionEnabled?: boolean;
  hasSessionAPI?: boolean;
  iamEnabled?: boolean;
}

interface UserProviderProps {
  user: User;
  children: ReactNode;
}

export enum CloudDiscoveryKey {
  aiEngineManagerApiUrl = 'aiEngineManagerApiUrl',
  featurestoreApiUrl = 'featurestoreApiUrl',
  telemetryApiUrl = 'telemetryApiUrl',
  mlopsApiUrl = 'mlopsApiUrl',
  orchestratorApiUrl = 'orchestratorApiUrl',
  notebookApiUrl = 'notebookApiUrl',
  secureStore = 'secureStore',
  authzUrl = 'authzUrl',
  mcAdminCenterApiUrl = 'mcAdminCenterApiUrl',
  auditTrailApiUrl = 'auditTrailApiUrl',
  h2oCloudEnvironment = 'h2oCloudEnvironment',
  providerUrl = 'providerUrl',
  platformClientId = 'platformClientId',
  stsDriveUrl = 'stsDriveUrl',
  userDriveUrl = 'userDriveUrl',
  workspaceDriveUrl = 'workspaceDriveUrl',
  modelHub = 'modelHub',
  sessionServer = 'sessionServer',
  workflowApiUrl = 'workflowApiUrl',
  h2oGpte = 'h2oGpte',
  hacAssistantApiUrl = 'hacAssistantApiUrl',
}

export type CloudPlatformDiscoveryConfig = Partial<Record<CloudDiscoveryKey, string>>;

export const UserContext = createContext<User>({} as User);

export const UserProvider = ({ children, user }: UserProviderProps) => (
  <UserContext.Provider value={user}>{children}</UserContext.Provider>
);

export interface EnvironmentAndMenu extends GetEnvironmentDataResponse {
  h2oCloudVersion?: string;
  menu?: AppStoreMenuInfo;
}

interface EnvProviderProps {
  env: EnvironmentAndMenu | undefined;
  children: ReactNode;
}

export const EnvContext = createContext<EnvironmentAndMenu | undefined>({} as EnvironmentAndMenu);

export const EnvProvider = ({ children, env }: EnvProviderProps) => (
  <EnvContext.Provider value={env}>{children}</EnvContext.Provider>
);

interface CloudPlatformDiscoveryProviderProps {
  discovery: CloudPlatformDiscoveryConfig;
  children: ReactNode;
}

export const CloudPlatformDiscoveryContext = createContext<CloudPlatformDiscoveryConfig | undefined>(
  {} as CloudPlatformDiscoveryConfig
);

export const CloudPlatformDiscoveryProvider = ({ children, discovery }: CloudPlatformDiscoveryProviderProps) => (
  <CloudPlatformDiscoveryContext.Provider value={discovery}>{children}</CloudPlatformDiscoveryContext.Provider>
);

interface LeftPanelProviderProps {
  setLeftPanelProps: Dispatch<SetStateAction<ILeftPanelProps | undefined>>;
  children: ReactNode;
}

export const LeftPanelContext = createContext<Dispatch<SetStateAction<ILeftPanelProps | undefined>>>(() => null);

export const LeftPanelProvider = ({ children, setLeftPanelProps }: LeftPanelProviderProps) => (
  <LeftPanelContext.Provider value={setLeftPanelProps}>{children}</LeftPanelContext.Provider>
);

interface BannerProviderProps {
  children: ReactNode;
}

export const BannerContext = createContext<{
  banners: Banner[];
  dispatch: Dispatch<BannerAction>;
}>({ banners: [], dispatch: () => null });

export const BannerProvider = ({ children }: BannerProviderProps) => {
  const [bannersById, dispatch] = useReducer((state: Map<string, Banner>, action: BannerAction) => {
    switch (action.type) {
      case `Add`:
        state = new Map(state);
        state.set(action.payload.id, { ...action.payload });
        break;
      case `Remove`:
        if (state.has(action.payload.id)) {
          state = new Map(state);
          state.delete(action.payload.id);
        }
        break;
    }
    return state;
  }, new Map<string, Banner>());
  const value = useMemo(() => ({ banners: Array.from(bannersById.values()), dispatch }), [bannersById]);
  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
};

/**
 * An utility context to manage expanded/collapsed state or components
 */
export type ItemsFlagContext = {
  readonly defaultOn: boolean;
  readonly deviatesFromDefault: Set<string>;
  allOff: () => void;
  allOn: () => void;
  isOn: (id: string) => boolean;
  setFlag: (id: string, flag: boolean) => void;
  setItems: (ids: string[], flag: boolean) => void;
  toggleFlag: (id: string) => void;
};

const throwWrapInContextError = () => {
  throw new Error('You must wrap your component in a itemsFlagContext.Provider');
};

export const itemsFlagContext = createContext<ItemsFlagContext>({
  defaultOn: false,
  deviatesFromDefault: new Set<string>(),
  allOff: throwWrapInContextError,
  allOn: throwWrapInContextError,
  isOn: throwWrapInContextError,
  setFlag: throwWrapInContextError,
  setItems: throwWrapInContextError,
  toggleFlag: throwWrapInContextError,
});

type ItemsFlagState = {
  defaultOn: boolean;
  deviatesFromDefault: Set<string>;
};

type ItemsFlagAction =
  | { type: `allOff` }
  | { type: `allOn` }
  | {
      type: `setFlag`;
      payload: { id: string; flag: boolean };
    }
  | { type: `setItems`; payload: { ids: string[]; flag: boolean } }
  | { type: `toggleFlag`; payload: { id: string } };

const itemsFlagReducer = (state: ItemsFlagState, action: ItemsFlagAction): ItemsFlagState => {
  switch (action.type) {
    case `allOff`:
      return { defaultOn: false, deviatesFromDefault: new Set<string>() };
    case `allOn`:
      return { defaultOn: true, deviatesFromDefault: new Set<string>() };
    case `setFlag`: {
      const { id, flag } = action.payload;
      const idDeviates = state.deviatesFromDefault.has(id);
      const currentFlag = idDeviates ? !state.defaultOn : state.defaultOn;
      if (currentFlag === flag) return state; // no change needed
      const newDeviatesFromDefault = new Set(state.deviatesFromDefault);
      if (idDeviates) {
        newDeviatesFromDefault.delete(id);
      } else {
        newDeviatesFromDefault.add(id);
      }
      return { ...state, deviatesFromDefault: newDeviatesFromDefault };
    }
    case `setItems`: {
      const { ids, flag } = action.payload;
      const newDeviatesFromDefault = new Set(state.deviatesFromDefault);
      if (flag === state.defaultOn) {
        ids.forEach((id) => {
          newDeviatesFromDefault.delete(id);
        });
      } else {
        ids.forEach((id) => {
          newDeviatesFromDefault.add(id);
        });
      }
      return { ...state, deviatesFromDefault: newDeviatesFromDefault };
    }
    case `toggleFlag`: {
      const { id } = action.payload;
      const idDeviates = state.deviatesFromDefault.has(id);
      const newDeviatesFromDefault = new Set(state.deviatesFromDefault);
      if (idDeviates) {
        newDeviatesFromDefault.delete(id);
      } else {
        newDeviatesFromDefault.add(id);
      }
      return { ...state, deviatesFromDefault: newDeviatesFromDefault };
    }
    default: {
      const exhaustiveCheck: never = action;
      throw new Error(`Unknown action type: ${exhaustiveCheck}`);
    }
  }
};

export const useItemsFlag = (initialFlag = false): ItemsFlagContext => {
  const [itemsFlagState, dispatchItemsFlagState] = useReducer(itemsFlagReducer, {
    defaultOn: initialFlag ?? false,
    deviatesFromDefault: new Set<string>(),
  });
  const allOff = useCallback(() => {
    dispatchItemsFlagState({ type: `allOff` });
  }, []);
  const allOn = useCallback(() => {
    dispatchItemsFlagState({ type: `allOn` });
  }, []);
  const isOn = useCallback(
    (id: string) => {
      const idDeviates = itemsFlagState.deviatesFromDefault.has(id);
      return idDeviates ? !itemsFlagState.defaultOn : itemsFlagState.defaultOn;
    },
    [itemsFlagState]
  );
  const setFlag = useCallback((id: string, flag: boolean) => {
    dispatchItemsFlagState({ type: `setFlag`, payload: { id, flag } });
  }, []);
  const setItems = useCallback((ids: string[], flag: boolean) => {
    dispatchItemsFlagState({ type: `setItems`, payload: { ids, flag } });
  }, []);
  const toggleFlag = useCallback((id: string) => {
    dispatchItemsFlagState({ type: `toggleFlag`, payload: { id } });
  }, []);
  return {
    defaultOn: itemsFlagState.defaultOn,
    deviatesFromDefault: itemsFlagState.deviatesFromDefault,
    allOff,
    allOn,
    isOn,
    setFlag,
    setItems,
    toggleFlag,
  };
};

export type CollapsedContext = {
  collapseAll: () => void;
  expandAll: () => void;
  isCollapsed: (id: string) => boolean;
  setCollapsed: (id: string, flag: boolean) => void;
  setCollapsedItems: (ids: string[], flag: boolean) => void;
  toggleCollapsed: (id: string) => void;
};

export const useCollapsed = (initiallyAllCollapsed = true): CollapsedContext => {
  const {
    allOff: expandAll,
    allOn: collapseAll,
    isOn: isCollapsed,
    setFlag: setCollapsed,
    setItems: setCollapsedItems,
    toggleFlag: toggleCollapsed,
  } = useItemsFlag(initiallyAllCollapsed);
  return { collapseAll, expandAll, isCollapsed, setCollapsed, setCollapsedItems, toggleCollapsed };
};

export type SelectedContext = {
  readonly defaultSelected: boolean;
  readonly deviatesFromDefault: Set<string>;
  selectAll: () => void;
  emptySelection: () => void;
  isSelected: (id: string) => boolean;
  setSelected: (id: string, flag: boolean) => void;
  setSelectedItems: (ids: string[], flag: boolean) => void;
  toggleSelected: (id: string) => void;
};

export const useSelected = (initiallyAllSelected = false): SelectedContext => {
  const {
    defaultOn: defaultSelected,
    deviatesFromDefault,
    allOff: emptySelection,
    allOn: selectAll,
    isOn: isSelected,
    setFlag: setSelected,
    setItems: setSelectedItems,
    toggleFlag: toggleSelected,
  } = useItemsFlag(initiallyAllSelected);
  return {
    defaultSelected,
    deviatesFromDefault,
    emptySelection,
    isSelected,
    selectAll,
    setSelected,
    setSelectedItems,
    toggleSelected,
  };
};

export const ItemsFlagProvider = ({ children, initialFlag }: { children: ReactNode; initialFlag?: boolean }) => {
  const itemsContextValue = useItemsFlag(initialFlag);
  return <itemsFlagContext.Provider value={itemsContextValue}>{children}</itemsFlagContext.Provider>;
};

export interface AdminCenterTimeFilterContextType {
  startDate: Date;
  endDate: Date;
  selectedTimeFrame: string;
  setTimeFilter: (startDate: Date, endDate: Date, timeFrame?: string) => void;
}

export const AdminCenterTimeFilterContext = createContext<AdminCenterTimeFilterContextType | undefined>(undefined);

// The picked days are whole days in the user's own timezone, and the backend filters on instants —
// so the range has to run to the last millisecond of the end day. Leaving it at midnight admits
// only the end day's first hour, which reads as a cliff at the right edge of every chart.
const adminCenterStartOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const adminCenterEndOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

export const AdminCenterTimeFilterProvider = ({ children }: { children: ReactNode }) => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(today.getDate() - 30);
  const [startDate, setStartDate] = useState<Date>(adminCenterStartOfDay(oneMonthAgo));
  const [endDate, setEndDate] = useState<Date>(adminCenterEndOfDay(today));
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>('custom');

  const setTimeFilter = useCallback((newStartDate: Date, newEndDate: Date, timeFrame = 'custom') => {
    setStartDate(adminCenterStartOfDay(newStartDate));
    setEndDate(adminCenterEndOfDay(newEndDate));
    setSelectedTimeFrame(timeFrame);
  }, []);

  const value = useMemo(
    () => ({
      startDate,
      endDate,
      selectedTimeFrame,
      setTimeFilter,
    }),
    [startDate, endDate, selectedTimeFrame, setTimeFilter]
  );

  return <AdminCenterTimeFilterContext.Provider value={value}>{children}</AdminCenterTimeFilterContext.Provider>;
};
